<template>
  <div id="accordionTourBeforeTour" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingTourBeforeTour" class="accordion-header">
        <button
          aria-controls="collapseTourBeforeTour"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseTourBeforeTour"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex">
            <div class="bg-secondary rounded-xs h-fit-content me-8">
              <v-icon icon="contact" size="xxl" space="p-12" />
            </div>

            <div>
              <div class="h4 ff-secondary mb-0">{{ $t("clientTour.beforeTour") }}</div>
              <div class="fw-light accordion-subtitle">{{ $t("clientTour.beforeTourSubtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseTourBeforeTour"
        aria-labelledby="headingTourBeforeTour"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionTourBrforeTour"
      >
        <div class="accordion-body pb-32 pb-xl-48">
          <nav class="translations-tab">
            <div id="nav-tab" class="nav nav-tabs" role="tablist">
              <v-button
                v-for="(language, index) in languages"
                :id="`nav-template-day-update-basic-information-${language.key}`"
                :key="index"
                :aria-controls="`template-day-update-basic-information-${language.key}`"
                :class="{ active: language.shortName === currentLanguage }"
                :data-bs-target="`#template-day-update-basic-information-${language.key}`"
                aria-selected="true"
                class="nav-tab-link py-8 px-16 me-8"
                data-bs-toggle="tab"
                role="tab"
                size="sm"
                variant="outline-primary"
                @click="currentLanguage = language.shortName"
              >
                {{ language.label }}
              </v-button>
            </div>
          </nav>
          <form id="case-tour-update-before-update-form" class="d-flex flex-column justify-content-between h-100">
            <fieldset v-for="language in languages" :key="language.shortName">
              <div class="form-outline" v-if="currentLanguage === language.shortName">
                <label class="label">
                  <span>{{ $t("clientTour.beforeTour") }}</span>
                </label>
                <v-html-editor
                  :id="'case-tour-update-before-update-' + language.shortName"
                  ref="case-tour-update-before-update-ref"
                  :value="beforeTrip[language.shortName]?.description"
                  @input="handleInput(language.shortName, $event)"
                  @media-uploaded="editorMedia.push($event)"
                />
              </div>
            </fieldset>

            <v-button
              :is-loading="accepted"
              :disabled="disableButton"
              class="btn-icon w-fit align-self-end mt-24"
              size="sm"
              variant="primary"
              @click="updateTemplate"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="sm" space="ms-12" />
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, computed } from "vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import api from "@/api";
import useFormValidation from "@/helpers/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import Template from "@/api/objects/Template";
import useConcepts from "@/helpers/Concepts";
import { mapConcepts } from "@/helpers/ConceptHelper";
import { useToast, POSITION } from "vue-toastification";

const props = defineProps({
  agency: {
    type: String,
    required: true,
  },
  template: {
    type: Object as () => Template,
    required: true,
  },
});

const emit = defineEmits(["reloadTemplate"]);
const toast = useToast();

// Build beforeTrip as a reactive object
const beforeTrip = reactive<{ [key: string]: { description: string } }>({
  en: { description: "" },
  nl: { description: "" },
  es: { description: "" },
});

const disableButton = ref<boolean>(true);
const editorMedia = ref<any[]>([]);
const currentLanguage = ref<string>(localStorage.getItem("language") || "en");

const concepts = useConcepts();
const languages = computed(() => {
  if (concepts.concepts.value && concepts.concepts.value["app.languages"]) {
    return mapConcepts(concepts.concepts.value["app.languages"]);
  }
  return [];
});

const form = useFormValidation("", beforeTrip);
const { accepted, toggleAccepted } = form;

// Helper to update disableButton state
function updateDisableButton() {
  disableButton.value = Object.values(beforeTrip).some((locale) => !locale.description || locale.description.trim() === "");
}

const beforeTripData = (props.template as any)?.beforeInformation?.translations || [];
beforeTripData.forEach((translation: { locale: string; description: string }) => {
  const { locale, description } = translation;
  if (beforeTrip[locale]) {
    beforeTrip[locale].description = description;
  }
});

const handleInput = (language: string, event: string) => {
  if (beforeTrip[language]) {
    beforeTrip[language].description = event;
  }
  updateDisableButton();
};

const updateTemplate = async () => {
  try {
    toggleAccepted();
    disableButton.value = true;

    const data = {
      name: props.template.name,
      travel_type: props.template.travelType,
      origin: props.template.originId,
      destination: props.template.destinationId,
      contact_text: props.template.contactText,
      terms_conditions_text: props.template.termsConditionsText,
      before_trip: beforeTrip,
    };

    const template = await api.activityService.updateActivity(props.agency, props.template.id, data);

    if (editorMedia.value.length > 0) {
      const editorMediaData = {
        media: editorMedia.value,
        model_type: "template",
        collection: "tinymce",
        model_id: props.template.id,
      };
      await api.media.confirm("s3", editorMediaData);
    }

    toast.success("Saved successfully", { position: POSITION.BOTTOM_RIGHT });
    emit("reloadTemplate");
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || e?.message || "An error occurred", {
      position: POSITION.BOTTOM_RIGHT,
    });
  } finally {
    toggleAccepted();
  }
};
</script>

<style scoped>
.btn-outline-primary.active {
  background-color: #d82770;
  border-color: #d82770;
  color: #fcfcfc;
  border-width: 1px;
  border-radius: 0.5rem 0.5rem 0 0;
  margin-top: 0.25rem;
  box-shadow: none;
  border-bottom: 0px;
}

.nav-tabs .nav-tab-link {
  border-width: 1px;
  border-radius: 0.5rem 0.5rem 0 0;
  margin-top: 0.25rem;
  box-shadow: none;
}

.nav-tabs {
  border-bottom: 1px solid #d82770 !important;
}
</style>
